import React, { useLayoutEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

/**
 * Four-line clamp, extracted verbatim from TCAIREMResourceRow (issue #472).
 * -webkit-box + WebkitLineClamp is what makes scrollHeight exceed clientHeight,
 * which is how overflow is detected below; changing it breaks the toggle.
 */
export const DESCRIPTION_CLAMP_SX = {
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
}

/**
 * Shared read-only resource description: clamped text plus a Show more/Show less
 * control that appears ONLY when the text actually overflows the clamp. An empty
 * or whitespace-only description renders nothing at all, so callers never need
 * their own guard.
 *
 * Props:
 *   description    - the text (may be undefined/null/empty)
 *   containerSx    - sx for the wrapping Box (e.g. TCAIREMStyle.rowBody)
 *   clampSx        - sx applied while collapsed (default DESCRIPTION_CLAMP_SX)
 *   typographyProps- extra props spread onto the Typography (e.g. { variant: 'p' }).
 *                    Any `sx` inside typographyProps is overridden by this component's
 *                    own sx (clampSx while collapsed, {} while expanded) -- pass
 *                    layout overrides via `clampSx` instead.
 *   remeasureKey   - changing this re-runs the overflow measurement; pass any value
 *                    whose change reflows the container (TCAIREMResourceRow passes
 *                    playerOpen, because opening the player changes the row width)
 */
const ResourceDescription = ({
    description,
    containerSx,
    clampSx = DESCRIPTION_CLAMP_SX,
    typographyProps = {},
    remeasureKey,
}) => {
    const textRef = useRef(null)
    const [expanded, setExpanded] = useState(false)
    const [overflowing, setOverflowing] = useState(false)

    useLayoutEffect(() => {
        // Only a clamped element can be measured for overflow, so short-circuit
        // once expanded. Hooks must run unconditionally, hence this sits above
        // the empty-guard return below.
        if (expanded) return
        const el = textRef.current
        if (el) setOverflowing(el.scrollHeight > el.clientHeight)
    }, [description, expanded, remeasureKey])

    if (!(description || '').trim()) return null

    return (
        <Box sx={containerSx}>
            <Typography
                {...typographyProps}
                ref={textRef}
                sx={expanded ? {} : clampSx}
            >
                {description}
            </Typography>
            {overflowing && (
                <Button size='small' onClick={() => setExpanded(prev => !prev)}>
                    {expanded ? 'Show less' : 'Show more'}
                </Button>
            )}
        </Box>
    )
}

export default ResourceDescription
